import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
    useGetDeliveryRequestByIdQuery,
    useUpdateDeliveryRequestMutation,
    useCreateDeliveryRequestMutation
} from '../../../store/services/delivery-service';
import { useGetSimulationCollectionsQuery } from "../../../store/services/simulation-service";
import { useGetContentPacksQuery } from "../../../store/services/university-service";
import {
    useGetTenancyBasicsQuery,
    useLazyGetCountriesByTenancyIdQuery,
    useLazyGetDepartmentsByTenancyIdQuery,
    useLazyGetLocationsByTenancyIdQuery,
    useLazyGetCitiesByTenancyIdQuery,
    useLazyGetStatesByTenancyIdQuery,
    useLazyGetCompanyNamesByTenancyIdQuery,
} from "../../../store/services/tenancy.service";

import { formatDateForAPI, formatDate } from '../utils/dateUtils';
import { getDeliveryErrorMessage } from '../utils/errorUtils';

/**
 * Hook for managing calendar data and interactions
 * 
 * @param {string} mode - 'edit', 'create', or 'preset'
 * @param {string} deliveryGroupId - ID of the delivery group (for edit mode)
 * @param {Object} presetData - Preset data (for preset mode)
 * @param {string} startDate - Start date (for preset mode)
 * @param {Function} showNotification - Function to show notifications
 * @returns {Object} - Calendar data and handlers
 */
export const useCalendarData = (mode, deliveryGroupId, presetData, startDate, showNotification) => {
    const { tenancyId } = useSelector((state) => state.tenancy);
    const location = useLocation();

    // State management
    const [monthsToDisplay, setMonthsToDisplay] = useState([]);
    const [deliveryGroupName, setDeliveryGroupName] = useState('');
    const [deliveryGroup, setDeliveryGroup] = useState(null);
    const [originalItems, setOriginalItems] = useState([]);
    const [calendarItems, setCalendarItems] = useState([]);
    const [attemptedSave, setAttemptedSave] = useState(false);
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [showBanner, setShowBanner] = useState(true);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isMoveMode, setIsMoveMode] = useState(false);
    const [itemToMove, setItemToMove] = useState(null);

    // recipient state
    const [recipientData, setRecipientData] = useState({
        recipientType: 'Tenancy',
        sendToId: '',
        displayName: 'All Users'
    });


    // Change tracking state
    const [changeTracker, setChangeTracker] = useState({
        updatedItems: [],
        itemsToRemove: [],
        newItems: []
    });

    // Enhanced form state to support random simulations, content packs, and batch sending
    const [formState, setFormState] = useState({
        contentId: '',
        contentType: 'Simulation',
        scheduledFor: '',
        selectedContentName: '',
        selectedCollectionId: '', // For random simulation collection selection
        isRandomSimulation: false, // Track if random simulation is selected
        selectedContentPackId: '', // For content pack selection
        isContentPack: false, // Track if content pack is selected
        contentName: '', // Content name for display
        // Batch send fields
        isBatchSend: false, // Toggle between direct send and batch send
        scheduledEndDate: '', // End date for batch sending (YYYY-MM-DD)
        sendingTimeStart: '', // Daily start time in HH:mm format
        sendingTimeEnd: '', // Daily end time in HH:mm format
        pauseWeekends: false // Whether to pause sending on weekends
    });

    // Multi-item selection state
    const [multiItemSelectState, setMultiItemSelectState] = useState({
        isOpen: false,
        items: [],
        date: null,
        readOnly: false
    });

    // Destructure state for cleaner code
    const { updatedItems, itemsToRemove, newItems } = changeTracker;

    const {
        data: deliveryRequestData,
        isLoading: isLoadingRequest,
        error: requestError,
        refetch: refetchDeliveryRequest
    } = useGetDeliveryRequestByIdQuery(
        {
            tenancyId,
            requestId: deliveryGroupId
        },
        { skip: !tenancyId || !deliveryGroupId || mode === 'create' }
    );

    const { data: collectionData } = useGetSimulationCollectionsQuery({
        perPage: 1000,
        page: 0,
        searchTerm: "",
        tenancyId: tenancyId,
        published: true
    });

    const { data: contentPackData } = useGetContentPacksQuery({
        perPage: 1000,
        page: 0,
        searchTerm: "",
        tenancyId: tenancyId
    });

    // Fetch tenancy data for group name lookup
    const { data: tenancyData } = useGetTenancyBasicsQuery({
        tenancyId: tenancyId
    });

    // Lazy queries for additional data types (meta endpoints)
    const [triggerCountries, { data: countriesData, isLoading: isLoadingCountries }] = useLazyGetCountriesByTenancyIdQuery();
    const [triggerDepartments, { data: departmentsData, isLoading: isLoadingDepartments }] = useLazyGetDepartmentsByTenancyIdQuery();
    const [triggerLocations, { data: locationsData, isLoading: isLoadingLocations }] = useLazyGetLocationsByTenancyIdQuery();
    const [triggerCities, { data: citiesData, isLoading: isLoadingCities }] = useLazyGetCitiesByTenancyIdQuery();
    const [triggerStates, { data: statesData, isLoading: isLoadingStates }] = useLazyGetStatesByTenancyIdQuery();
    const [triggerCompanyNames, { data: companyNamesData, isLoading: isLoadingCompanyNames }] = useLazyGetCompanyNamesByTenancyIdQuery();

    // Check if any meta data is loading
    const isLoadingRecipientName = isLoadingCountries || isLoadingDepartments || isLoadingLocations || isLoadingCities || isLoadingStates || isLoadingCompanyNames;

    const [
        updateDeliveryRequest,
        { isLoading: isUpdating }
    ] = useUpdateDeliveryRequestMutation();

    const [
        createDeliveryRequest,
        { isLoading: isCreating }
    ] = useCreateDeliveryRequestMutation();

    // Get the appropriate meta data based on recipient type
    const getMetaData = useCallback((type) => {
        switch (type) {
            case 'Country': return countriesData;
            case 'Department': return departmentsData;
            case 'Location': return locationsData;
            case 'City': return citiesData;
            case 'State': return statesData;
            case 'CompanyName': return companyNamesData;
            default: return null;
        }
    }, [countriesData, departmentsData, locationsData, citiesData, statesData, companyNamesData]);

    // Helper function to get display name - resolves group UUIDs/IDs to names
    const getRecipientDisplayName = useCallback((type, sendToId) => {
        if (type === 'Tenancy') return 'All Users';
        if (type === 'User') return sendToId; // Email

        // For Group type (MS/Google synced groups), look up the group name from tenancy data
        if (type === 'Group' && tenancyData?.groups) {
            // Convert to string for comparison since sendToId is stored as string but userGroupId may be number
            const group = tenancyData.groups.find(g => String(g.userGroupId) === String(sendToId));
            if (group) {
                return group.name;
            }
        }

        // For other additional data types (Department, Location, City, Country, State, CompanyName)
        // Look up by ID from the corresponding meta endpoint data
        const metaTypes = ['Country', 'Department', 'Location', 'City', 'State', 'CompanyName'];
        if (metaTypes.includes(type)) {
            const metaData = getMetaData(type);
            if (metaData) {
                const item = metaData.find(m => String(m.id) === String(sendToId));
                if (item) {
                    return item.name;
                }
            }
        }

        // Fallback to sendToId if no lookup found
        return sendToId;
    }, [tenancyData, getMetaData]);

    // Check if user is admin
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        setIsAdmin(user && user.role === "Administrators");
    }, []);

    // Trigger the appropriate meta endpoint query based on recipient type
    useEffect(() => {
        if (mode === 'edit' && deliveryRequestData?.items?.length > 0 && tenancyId) {
            const recipientType = deliveryRequestData.items[0].recipientType;

            switch (recipientType) {
                case 'Country':
                    triggerCountries({ tenancyId });
                    break;
                case 'Department':
                    triggerDepartments({ tenancyId });
                    break;
                case 'Location':
                    triggerLocations({ tenancyId });
                    break;
                case 'City':
                    triggerCities({ tenancyId });
                    break;
                case 'State':
                    triggerStates({ tenancyId });
                    break;
                case 'CompanyName':
                    triggerCompanyNames({ tenancyId });
                    break;
                default:
                    break;
            }
        }
    }, [mode, deliveryRequestData, tenancyId, triggerCountries, triggerDepartments, triggerLocations, triggerCities, triggerStates, triggerCompanyNames]);

    // Initialise state with correct recipient 
    useEffect(() => {
        if (mode === 'create' || mode === 'preset') {
            // Get recipient from navigation state
            const navRecipient = location.state?.recipient;

            //console.log("attempting recipient selection", navRecipient)
            if (navRecipient) {
                setRecipientData({
                    recipientType: navRecipient.recipientType,
                    sendToId: navRecipient.sendToId,
                    displayName: navRecipient.displayName
                });

            }
        }
    }, [mode, location.state]);

    // Effect to extract recipient from first item in edit mode
    // Re-run when tenancyData loads to resolve group names
    useEffect(() => {
        if (mode === 'edit' && deliveryRequestData?.items?.length > 0) {
            const firstItem = deliveryRequestData.items[0];
            const displayName = getRecipientDisplayName(firstItem.recipientType, firstItem.sendToId);

            setRecipientData({
                recipientType: firstItem.recipientType,
                sendToId: firstItem.sendToId,
                displayName: displayName
            });
        }
    }, [mode, deliveryRequestData, getRecipientDisplayName]);

    // Process delivery request data when it's loaded in edit mode
    useEffect(() => {
        if (mode === 'edit' && deliveryRequestData) {
            setDeliveryGroup(deliveryRequestData.request);
            setOriginalItems(deliveryRequestData.items || []);

            // Set the delivery group name from the API data
            if (deliveryRequestData.request?.name) {
                setDeliveryGroupName(deliveryRequestData.request.name);
            }

            // Filter out cancelled items for display
            const activeItems = deliveryRequestData.items
                ? deliveryRequestData.items.filter(item => item.status !== 'Cancelled')
                : [];

            setCalendarItems(activeItems);

            // Reset tracking arrays when new data is loaded
            setChangeTracker({
                updatedItems: [],
                itemsToRemove: [],
                newItems: []
            });
            setUnsavedChanges(false);
        }
    }, [deliveryRequestData, mode]);

    // Initialize with preset data
    useEffect(() => {
        if (mode === 'preset' && presetData && startDate) {

            if (calendarItems.length > 0 && unsavedChanges) {
                console.log("Preset data already loaded and has changes, skipping reload");
                return; // Skip reloading if we already have items and changes
            }

            setDeliveryGroup(null);
            setOriginalItems([]);

            // Get weekend adjustment option from location state (if provided)
            const adjustForWeekends = location.state?.adjustForWeekends || false;

            // Get recipient data from location state (if provided)
            const navRecipient = location.state?.recipient;

            try {
                // Parse date properly - ensure it's a valid date
                const presetStartDate = new Date(startDate);

                if (isNaN(presetStartDate.getTime())) {
                    showNotification('Invalid start date provided', 'error');
                    return;
                }

                // Get today's date for comparison (set to start of day)
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                // Get tomorrow's date
                const tomorrow = new Date(today);
                tomorrow.setDate(today.getDate() + 1);

                // Convert preset items to calendar items
                const calendarItemsFromPreset = presetData.items.map((item, index) => {
                    try {
                        // Check if the item has the required properties for the new format
                        if (!('relativeMonth' in item) || !('dayOfMonth' in item)) {
                            console.warn('Deprecated format detected for item:', item);
                            throw new Error('Template uses deprecated format. Please update to the new format with relativeMonth and dayOfMonth properties.');
                        }

                        const itemDate = new Date(presetStartDate);

                        // Add the relative month (1-based, so subtract 1)
                        itemDate.setMonth(presetStartDate.getMonth() + (item.relativeMonth - 1));

                        // Try to set the day of month, handling month-end adjustments
                        let targetDay = item.dayOfMonth;
                        const daysInMonth = new Date(itemDate.getFullYear(), itemDate.getMonth() + 1, 0).getDate();

                        // Check if the day exists in this month (always adjust for month end)
                        if (targetDay > daysInMonth) {
                            // Use last day of month instead
                            targetDay = daysInMonth;
                        }

                        // Set the day
                        itemDate.setDate(targetDay);

                        // Handle weekend adjustment if requested
                        if (adjustForWeekends) {
                            const dayOfWeek = itemDate.getDay();
                            if (dayOfWeek === 0) { // Sunday
                                itemDate.setDate(itemDate.getDate() + 1); // Move to Monday
                            } else if (dayOfWeek === 6) { // Saturday
                                itemDate.setDate(itemDate.getDate() + 2); // Move to Monday
                            }
                        }

                        // Handle past dates for preset mode
                        // If the calculated date is in the past (before today), move it to tomorrow
                        const itemDateAtStartOfDay = new Date(itemDate);
                        itemDateAtStartOfDay.setHours(0, 0, 0, 0);

                        if (itemDateAtStartOfDay < today) {
                            // Set the date to tomorrow
                            itemDate.setTime(tomorrow.getTime());
                            console.log(`Moved past date ${formatDateForAPI(itemDateAtStartOfDay)} to tomorrow ${formatDateForAPI(itemDate)}`);
                        }

                        // Format date properly for API
                        const scheduledDate = formatDateForAPI(itemDate);

                        if (!scheduledDate) {
                            console.warn('Invalid date calculated for item:', item);
                            return null;
                        }

                        // Create a temporary ID for the new item
                        const tempId = `temp-${Date.now()}-${index}`;

                        // Calculate scheduledEndDate from batchDurationDays if batch send is enabled.
                        // batchDurationDays is an inclusive day count, so a 1-day batch ends on
                        // the start date (offset = N - 1). Guard on > 0 so a genuine 1-day batch
                        // (never a falsy 0) is not silently dropped.
                        let scheduledEndDate = null;
                        if (item.isBatchSend && item.batchDurationDays > 0) {
                            const endDate = new Date(itemDate);
                            endDate.setDate(endDate.getDate() + (item.batchDurationDays - 1));
                            scheduledEndDate = formatDateForAPI(endDate);
                        }

                        // Determine recipient - use navigation state recipient if available, otherwise default to tenancy
                        const itemRecipientType = navRecipient?.recipientType || "Tenancy";
                        const itemSendToId = navRecipient?.sendToId || tenancyId.toString();

                        return {
                            id: tempId,
                            requestId: null,
                            contentId: item.contentId,
                            contentName: item.contentName,
                            contentType: item.contentType,
                            recipientType: itemRecipientType,
                            sendToId: itemSendToId,
                            status: "Pending",
                            scheduledFor: scheduledDate,
                            sentAt: null,
                            error: null,
                            attempts: 0,
                            lastAttemptAt: null,
                            // Batch send fields
                            isBatchSend: item.isBatchSend || false,
                            scheduledEndDate: scheduledEndDate,
                            sendingTimeStart: item.sendingTimeStart || '',
                            sendingTimeEnd: item.sendingTimeEnd || '',
                            pauseWeekends: item.pauseWeekends || false
                        };
                    } catch (error) {
                        console.error('Error processing preset item:', error, item);
                        showNotification('This template uses a deprecated format. Please update to the latest version.', 'error');
                        return null;
                    }
                }).filter(item => item !== null);

                setCalendarItems(calendarItemsFromPreset);

                // Set delivery group name from preset
                setDeliveryGroupName(`${presetData.name} - ${new Date(startDate).toLocaleDateString()}`);

                const months = [];
                const currentDate = new Date(startDate);
                for (let i = 0; i < 25; i++) {
                    // Calculate month and year directly to avoid date adjustment issues
                    const monthValue = (currentDate.getMonth() + i) % 12;
                    const yearValue = currentDate.getFullYear() + Math.floor((currentDate.getMonth() + i) / 12);

                    months.push({
                        year: yearValue,
                        month: monthValue
                    });
                }
                setMonthsToDisplay(months);
                setUnsavedChanges(true);
            } catch (error) {
                console.error('Error processing preset:', error);
                showNotification('Error processing template data', 'error');
            }
        }
    }, [mode, presetData, startDate, tenancyId, location, showNotification, unsavedChanges]);

    // Initialize empty state for create mode
    useEffect(() => {
        if (mode === 'create') {
            setDeliveryGroup(null);
            setOriginalItems([]);
            setCalendarItems([]);
            setChangeTracker({
                updatedItems: [],
                itemsToRemove: [],
                newItems: []
            });

            // Generate 25 months from current date
            const months = [];
            const currentDate = new Date();
            for (let i = 0; i < 25; i++) {
                // Calculate month and year directly to avoid date adjustment issues
                const monthValue = (currentDate.getMonth() + i) % 12;
                const yearValue = currentDate.getFullYear() + Math.floor((currentDate.getMonth() + i) / 12);

                months.push({
                    year: yearValue,
                    month: monthValue
                });
            }
            setMonthsToDisplay(months);
        }
    }, [mode]);

    useEffect(() => {
        if (mode === 'create') return;

        // Handle case when there are no items - show 25 months from today
        if (!originalItems || originalItems.length === 0) {
            const months = [];
            const currentDate = new Date();
            for (let i = 0; i < 25; i++) {
                const monthValue = (currentDate.getMonth() + i) % 12;
                const yearValue = currentDate.getFullYear() + Math.floor((currentDate.getMonth() + i) / 12);
                months.push({
                    year: yearValue,
                    month: monthValue
                });
            }
            setMonthsToDisplay(months);
            return;
        }

        // Find earliest and latest scheduled dates
        let earliestDate = new Date();
        let latestDate = new Date(0); // Jan 1, 1970

        originalItems.forEach(item => {
            if (item.scheduledFor) {
                const itemDate = new Date(item.scheduledFor);
                if (itemDate < earliestDate) earliestDate = itemDate;
                if (itemDate > latestDate) latestDate = itemDate;
            }
        });

        // Start month = month of the earliest item
        const startMonth = new Date(earliestDate.getFullYear(), earliestDate.getMonth(), 1);

        // End month = 6 months after the latest item
        const endMonth = new Date(latestDate.getFullYear(), latestDate.getMonth() + 7, 0); // Go 6 months forward + 1 (to end of the 6th month)

        // Generate months from start to end
        const months = [];
        const currentMonth = new Date(startMonth);

        while (currentMonth <= endMonth) {
            months.push({
                year: currentMonth.getFullYear(),
                month: currentMonth.getMonth()
            });
            currentMonth.setMonth(currentMonth.getMonth() + 1);
        }

        setMonthsToDisplay(months);
    }, [originalItems, mode]);

    const itemsByDate = useMemo(() => {
        const map = new Map();

        calendarItems.forEach(item => {
            if (!item.scheduledFor) return;

            const dateKey = formatDate(item.scheduledFor);
            if (!map.has(dateKey)) {
                map.set(dateKey, []);
            }
            map.get(dateKey).push(item);
        });

        // Sort items by content type within each date
        map.forEach(items => {
            items.sort((a, b) => a.contentType.localeCompare(b.contentType));
        });

        return map;
    }, [calendarItems]);

    // Compute batch send date ranges for highlighting
    const batchRanges = useMemo(() => {
        const ranges = [];

        calendarItems.forEach(item => {
            if (item.isBatchSend && item.scheduledFor && item.scheduledEndDate) {
                const startDate = new Date(item.scheduledFor);
                startDate.setHours(0, 0, 0, 0);

                const endDate = new Date(item.scheduledEndDate);
                endDate.setHours(0, 0, 0, 0);

                // Only add if end date is after start date
                if (endDate >= startDate) {
                    ranges.push({
                        id: item.id,
                        startDate,
                        endDate,
                        contentType: item.contentType,
                        pauseWeekends: item.pauseWeekends
                    });
                }
            }
        });

        return ranges;
    }, [calendarItems]);

    useEffect(() => {
        if (requestError) {
            console.error("Error fetching journey:", requestError);
            showNotification(`Error loading calendar data: ${requestError.error || 'Unknown error'}`, 'error');
        }
    }, [requestError, showNotification]);

    // Track item updates for saving
    const trackItemUpdate = useCallback((updatedItem) => {
        // In create mode, all items are new
        if (mode === 'create' || mode === 'preset') {
            return;
        }

        // Check if this is a new item (it might already be in the newItems array)
        if (updatedItem.id.includes('temp-')) {
            setChangeTracker(prev => ({
                ...prev,
                newItems: prev.newItems.map(item =>
                    item.id === updatedItem.id ? updatedItem : item
                )
            }));
            return;
        }

        // Check if this is an existing item
        const isExistingItem = originalItems.some(item => item.id === updatedItem.id);

        if (isExistingItem) {
            // Find the original item to compare changes
            const originalItem = originalItems.find(item => item.id === updatedItem.id);

            setChangeTracker(prev => {
                // Check if we've already marked this item for update
                const existingUpdateIndex = prev.updatedItems.findIndex(
                    item => item.itemId === updatedItem.id
                );

                // Create the update data, only including properties that have changed
                const updateData = {
                    itemId: updatedItem.id,
                    status: updatedItem.status,
                    scheduledFor: updatedItem.scheduledFor,
                    recipientType: updatedItem.recipientType || recipientData.recipientType,
                    sendToId: updatedItem.sendToId || recipientData.sendToId || tenancyId.toString()
                };

                // If any content field changed, include all content fields to keep them in sync
                const contentChanged = originalItem && (
                    updatedItem.contentType !== originalItem.contentType ||
                    updatedItem.contentId !== originalItem.contentId ||
                    updatedItem.contentName !== originalItem.contentName
                );

                if (contentChanged) {
                    updateData.contentType = updatedItem.contentType;
                    updateData.contentId = updatedItem.contentId;
                    // For Course/Simulation, name IS the ID so contentId is a safe fallback.
                    // For RandomSimulation/ContentPack, contentId is a UUID - don't use it as display name.
                    if (updatedItem.contentType === 'Course' || updatedItem.contentType === 'Simulation') {
                        updateData.contentName = updatedItem.contentName || updatedItem.contentId;
                    } else {
                        updateData.contentName = updatedItem.contentName;
                    }
                }

                // Always include batch send fields so changes to delivery mode,
                // end date, time window, and weekend pause are persisted
                updateData.isBatchSend = updatedItem.isBatchSend || false;
                updateData.scheduledEndDate = updatedItem.scheduledEndDate || null;
                updateData.sendingTimeStart = updatedItem.sendingTimeStart || '';
                updateData.sendingTimeEnd = updatedItem.sendingTimeEnd || '';
                updateData.pauseWeekends = updatedItem.pauseWeekends || false;

                if (existingUpdateIndex >= 0) {
                    // Update the existing update
                    const newUpdatedItems = [...prev.updatedItems];
                    newUpdatedItems[existingUpdateIndex] = updateData;

                    return {
                        ...prev,
                        updatedItems: newUpdatedItems
                    };
                } else {
                    // Add a new update
                    return {
                        ...prev,
                        updatedItems: [...prev.updatedItems, updateData]
                    };
                }
            });
        }
    }, [originalItems, tenancyId, mode, recipientData]);

   

    // Handler to update recipient
    const handleRecipientChange = useCallback((newRecipientData) => {
        setRecipientData(newRecipientData);

        // Update all existing calendar items with new recipient
        setCalendarItems(prev => {
            const updatedItems = prev.map(item => ({
                ...item,
                recipientType: newRecipientData.recipientType,
                sendToId: newRecipientData.sendToId
            }));

            // In edit mode, track all existing items so the recipient change is persisted
            if (mode === 'edit') {
                updatedItems.forEach(item => {
                    trackItemUpdate(item);
                });
            }

            return updatedItems;
        });

        setUnsavedChanges(true);
    }, [mode, trackItemUpdate]);

    const handleContentTypeChange = useCallback((e) => {
        const newContentType = e.target.value;
        setFormState(prev => ({
            ...prev,
            contentType: newContentType,
            selectedContentName: '',
            contentId: '',
            contentName: '',
            selectedCollectionId: '', // Reset collection selection
            isRandomSimulation: false, // Reset random simulation flag
            selectedContentPackId: '', // Reset content pack selection
            isContentPack: false // Reset content pack flag
        }));
    }, []);

    const handleFormChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormState(prev => ({
            ...prev,
            [name]: value
        }));
    }, []);

    // Enhanced content selection handler
    // Also update the handleContentSelection function to ensure proper state management
    const handleContentSelection = useCallback((e) => {
        const selectedName = e.target.value;
        console.log('handleContentSelection called with:', selectedName);

        if (selectedName === 'Random Simulation') {
            // Handle Random Simulation selection
            setFormState(prev => {
                const newState = {
                    ...prev,
                    selectedContentName: selectedName,
                    contentId: '', // Will be set when collection is selected
                    contentName: '', // Will be set when collection is selected
                    isRandomSimulation: true,
                    isContentPack: false,
                    selectedCollectionId: '', // Reset collection selection
                    selectedContentPackId: '' // Reset content pack selection
                };
                console.log('Setting form state for Random Simulation:', newState);
                return newState;
            });
        } else if (selectedName === 'Content Pack') {
            // Handle Content Pack selection
            setFormState(prev => {
                const newState = {
                    ...prev,
                    selectedContentName: selectedName,
                    contentId: '', // Will be set when content pack is selected
                    contentName: '', // Will be set when content pack is selected
                    isRandomSimulation: false,
                    isContentPack: true,
                    selectedCollectionId: '', // Reset collection selection
                    selectedContentPackId: '' // Reset content pack selection
                };
                console.log('Setting form state for Content Pack:', newState);
                return newState;
            });
        } else {
            // Handle regular simulation or course selection
            setFormState(prev => {
                const newState = {
                    ...prev,
                    selectedContentName: selectedName,
                    contentId: selectedName,
                    contentName: selectedName,
                    isRandomSimulation: false,
                    isContentPack: false,
                    selectedCollectionId: '', // Reset collection selection
                    selectedContentPackId: '' // Reset content pack selection
                };
                console.log('Setting form state for regular content:', newState);
                return newState;
            });
        }
    }, []);

    // Collection selection handler for random simulations
    const handleCollectionSelection = useCallback((e) => {
        const selectedCollectionName = e.target.value;
        console.log('handleCollectionSelection called with:', selectedCollectionName);
        console.log('Available collections:', collectionData);

        const selectedCollection = collectionData?.find(col => col.collectionName === selectedCollectionName);
        console.log('Found collection:', selectedCollection);

        if (selectedCollection) {
            setFormState(prev => {
                const newState = {
                    ...prev,
                    selectedCollectionId: selectedCollection.internalId,
                    contentId: selectedCollection.internalId, // Use internalId for RandomSimulation
                    contentName: selectedCollectionName // Store collection name for display
                };
                console.log('Updated form state:', newState);
                return newState;
            });
        } else {
            console.warn('Collection not found:', selectedCollectionName);
        }
    }, [collectionData]);

    // Content pack selection handler
    const handleContentPackSelection = useCallback((e) => {
        const selectedContentPackName = e.target.value;
        console.log('handleContentPackSelection called with:', selectedContentPackName);
        console.log('Available content packs:', contentPackData);

        const selectedContentPack = contentPackData?.find(pack => pack.contentPackName === selectedContentPackName);
        console.log('Found content pack:', selectedContentPack);

        if (selectedContentPack) {
            setFormState(prev => {
                const newState = {
                    ...prev,
                    selectedContentPackId: selectedContentPack.internalId,
                    contentId: selectedContentPack.internalId, // Use internalId for ContentPack
                    contentName: selectedContentPackName // Store content pack name for display
                };
                console.log('Updated form state:', newState);
                return newState;
            });
        } else {
            console.warn('Content pack not found:', selectedContentPackName);
        }
    }, [contentPackData]);

    // Handler for batch send field changes
    const handleBatchSendChange = useCallback((fieldName, value) => {
        setFormState(prev => ({
            ...prev,
            [fieldName]: value
        }));
    }, []);

    // Reset form state for adding a new item on a specific date
    // Used when opening the add modal from multiSelect or other contexts
    const resetFormStateForNewItem = useCallback((date) => {
        setSelectedDate(date);
        setFormState(prev => ({
            ...prev,
            contentId: '',
            contentType: 'Simulation',
            scheduledFor: formatDate(date),
            selectedContentName: '',
            selectedCollectionId: '',
            isRandomSimulation: false,
            selectedContentPackId: '',
            isContentPack: false,
            contentName: '',
            // Reset batch send fields for new items
            isBatchSend: false,
            scheduledEndDate: '',
            sendingTimeStart: '',
            sendingTimeEnd: '',
            pauseWeekends: false
        }));
    }, []);

    const handleMoveItemToDate = useCallback((date) => {
        if (!itemToMove) return;

        // Check if target date is today or in the past
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const targetDate = new Date(date);
        targetDate.setHours(0, 0, 0, 0);

        if (targetDate <= today) {
            showNotification("Cannot move items to past dates or today", "error");
            return;
        }

        // Format date properly for API
        const scheduledDate = formatDateForAPI(date);

        // Create the updated item
        const updatedItem = {
            ...itemToMove,
            scheduledFor: scheduledDate
        };

        // For batch send items, also shift the end date by the same amount
        if (itemToMove.isBatchSend && itemToMove.scheduledEndDate) {
            // Calculate the difference in days between original and new start date
            const originalStartDate = new Date(itemToMove.scheduledFor);
            originalStartDate.setHours(0, 0, 0, 0);

            const daysDifference = Math.round((targetDate.getTime() - originalStartDate.getTime()) / (1000 * 60 * 60 * 24));

            // Apply the same shift to the end date
            const originalEndDate = new Date(itemToMove.scheduledEndDate);
            originalEndDate.setHours(0, 0, 0, 0);
            originalEndDate.setDate(originalEndDate.getDate() + daysDifference);

            updatedItem.scheduledEndDate = formatDateForAPI(originalEndDate);
        }

        // Update in calendar items
        setCalendarItems(prev =>
            prev.map(item => item.id === itemToMove.id ? updatedItem : item)
        );

        // Track for API update
        trackItemUpdate(updatedItem);

        // Exit move mode
        setIsMoveMode(false);
        setItemToMove(null);

        setUnsavedChanges(true);

        // Show success notification
        showNotification('Item moved successfully', 'success');
    }, [itemToMove, showNotification, trackItemUpdate]);

    // Handle day click based on current mode
    const handleDayClick = useCallback((date, itemsAtDate = []) => {
        // First check if the date is today or in the past
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const cellDate = new Date(date);
        cellDate.setHours(0, 0, 0, 0);

        const isPastOrToday = cellDate <= today;

        // Proceed based on date validation
        if (isPastOrToday) {
            // Only allow viewing existing items for past dates
            if (itemsAtDate.length >= 1) {
                // Allow viewing but not editing
                setMultiItemSelectState({
                    isOpen: true,
                    items: itemsAtDate,
                    date: date,
                    readOnly: true // Add readOnly flag for past dates
                });
            } else {
                // For empty past cells, show notification that past dates can't be modified
                showNotification("Cannot add items to past dates or today", "warning");
            }
            return;
        }

        // Proceed normally for future dates
        if (isMoveMode && itemToMove) {
            // Move the item to the clicked date
            handleMoveItemToDate(date);
        } else if (itemsAtDate.length >= 1) {
            // If one or more items on this date, open selection modal
            setMultiItemSelectState({
                isOpen: true,
                items: itemsAtDate,
                date: date,
                readOnly: false
            });
        } else {
            // Normal mode - open add modal for empty cell
            setSelectedDate(date);
            setFormState(prev => ({
                ...prev,
                contentId: '',
                contentType: 'Simulation',
                scheduledFor: formatDate(date),
                selectedContentName: '',
                selectedCollectionId: '',
                isRandomSimulation: false,
                selectedContentPackId: '',
                isContentPack: false,
                contentName: '',
                // Reset batch send fields for new items
                isBatchSend: false,
                scheduledEndDate: '',
                sendingTimeStart: '',
                sendingTimeEnd: '',
                pauseWeekends: false
            }));
        }
    }, [isMoveMode, itemToMove, handleMoveItemToDate, showNotification]);

    // Handle item click to edit - support random simulations and content packs
    const handleItemClick = useCallback((item) => {
        if (isMoveMode) return;

        const isPastDate = new Date(item.scheduledFor) <= new Date();
        if (isPastDate) {
            showNotification("Items scheduled for past dates or today cannot be edited", "warning");
            return;
        }

        setSelectedItem(item);

        // Helper to extract batch send fields from item
        const getBatchSendFields = (item) => ({
            isBatchSend: item.isBatchSend || false,
            scheduledEndDate: item.scheduledEndDate ? formatDate(item.scheduledEndDate) : '',
            sendingTimeStart: item.sendingTimeStart || '',
            sendingTimeEnd: item.sendingTimeEnd || '',
            pauseWeekends: item.pauseWeekends || false
        });

        // Determine form state based on item type
        if (item.contentType === 'RandomSimulation') {
            // Handle RandomSimulation items
            const collection = collectionData?.find(col => col.internalId === item.contentId);
            setFormState({
                contentId: item.contentId,
                contentType: 'Simulation', // Show as Simulation in dropdown
                contentName: item.contentName,
                scheduledFor: formatDate(item.scheduledFor),
                selectedContentName: 'Random Simulation', // Select Random Simulation option
                selectedCollectionId: item.contentId, // Collection internalId
                isRandomSimulation: true,
                selectedContentPackId: '',
                isContentPack: false,
                ...getBatchSendFields(item)
            });
        } else if (item.contentType === 'ContentPack') {
            // Handle ContentPack items - ContentPack is a subtype of Course
            const contentPack = contentPackData?.find(pack => pack.internalId === item.contentId);
            setFormState({
                contentId: item.contentId,
                contentType: 'Course', // Show as Course in dropdown (ContentPack is a subtype of Course)
                contentName: item.contentName,
                scheduledFor: formatDate(item.scheduledFor),
                selectedContentName: 'Content Pack', // Select Content Pack subtype option
                selectedCollectionId: '',
                isRandomSimulation: false,
                selectedContentPackId: item.contentId, // ContentPack internalId
                isContentPack: true,
                ...getBatchSendFields(item)
            });
        } else {
            // Handle regular simulation and course items
            setFormState({
                contentId: item.contentId,
                contentType: item.contentType,
                contentName: item.contentName,
                scheduledFor: formatDate(item.scheduledFor),
                selectedContentName: item.contentName || item.contentId,
                selectedCollectionId: '',
                isRandomSimulation: false,
                selectedContentPackId: '',
                isContentPack: false,
                ...getBatchSendFields(item)
            });
        }
    }, [isMoveMode, showNotification, collectionData, contentPackData]);

    // Enter move mode from edit modal
    const handleEnterMoveMode = useCallback(() => {
        if (!selectedItem) return;

        setIsMoveMode(true);
        setItemToMove(selectedItem);

        // Also close the multi-item select modal
        setMultiItemSelectState(prev => ({
            ...prev,
            isOpen: false
        }));

        showNotification('Select a day to move this item to', 'info');
    }, [selectedItem, showNotification]);

    const handleCancelMoveMode = useCallback(() => {
        setIsMoveMode(false);
        setItemToMove(null);
    }, []);

    // Handle add item with random simulation and content pack support
    const handleAddItem = useCallback(() => {
        const tempId = `temp-${Date.now()}`;
        const scheduledDate = formatDateForAPI(formState.scheduledFor);

        // Determine content properties based on selection
        let contentId, contentType, contentName;

        if (formState.isRandomSimulation) {
            // Random simulation
            contentId = formState.selectedCollectionId;
            contentType = 'RandomSimulation';
            contentName = formState.contentName;
        } else if (formState.isContentPack) {
            // Content pack
            contentId = formState.selectedContentPackId;
            contentType = 'ContentPack';
            contentName = formState.contentName;
        } else {
            // Regular simulation or course
            contentId = formState.contentId;
            contentType = formState.contentType;
            contentName = formState.selectedContentName;
        }

        // Build batch send fields if enabled
        const batchSendFields = formState.isBatchSend ? {
            isBatchSend: true,
            scheduledEndDate: formState.scheduledEndDate ? formatDateForAPI(formState.scheduledEndDate) : null,
            sendingTimeStart: formState.sendingTimeStart || '',
            sendingTimeEnd: formState.sendingTimeEnd || '',
            pauseWeekends: formState.pauseWeekends || false
        } : {
            isBatchSend: false,
            scheduledEndDate: null,
            sendingTimeStart: '',
            sendingTimeEnd: '',
            pauseWeekends: false
        };

        const newItem = {
            id: tempId,
            requestId: mode === 'edit' ? deliveryGroupId : null,
            contentId: contentId,
            contentType: contentType,
            contentName: contentName,
            recipientType: recipientData.recipientType,
            sendToId: recipientData.sendToId || tenancyId.toString(),
            status: "Pending",
            scheduledFor: scheduledDate,
            sentAt: null,
            error: null,
            attempts: 0,
            lastAttemptAt: null,
            ...batchSendFields
        };

        setCalendarItems(prev => [...prev, newItem]);

        if (mode === 'edit') {
            setChangeTracker(prev => ({
                ...prev,
                newItems: [...prev.newItems, {
                    contentId: contentId,
                    contentType: contentType,
                    contentName: contentName,
                    recipientType: recipientData.recipientType,
                    sendToId: recipientData.sendToId || tenancyId.toString(),
                    scheduledFor: scheduledDate,
                    ...batchSendFields
                }]
            }));
        }

        if (mode === 'create' && showBanner) {
            setShowBanner(false);
        }

        setUnsavedChanges(true);
    }, [formState, deliveryGroupId, tenancyId, mode, showBanner, recipientData]);

    // Handle update item with random simulation and content pack support
    const handleUpdateItem = useCallback(() => {
        if (!selectedItem) return;

        const scheduledDate = formatDateForAPI(formState.scheduledFor);

        // Determine content properties based on selection
        let contentId, contentType, contentName;

        if (formState.isRandomSimulation) {
            // Random simulation
            contentId = formState.selectedCollectionId;
            contentType = 'RandomSimulation';
            contentName = formState.contentName;
        } else if (formState.isContentPack) {
            // Content pack
            contentId = formState.selectedContentPackId;
            contentType = 'ContentPack';
            contentName = formState.contentName;
        } else {
            // Regular simulation or course
            contentId = formState.contentId;
            contentType = formState.contentType;
            contentName = formState.selectedContentName;
        }

        // Build batch send fields if enabled
        const batchSendFields = formState.isBatchSend ? {
            isBatchSend: true,
            scheduledEndDate: formState.scheduledEndDate ? formatDateForAPI(formState.scheduledEndDate) : null,
            sendingTimeStart: formState.sendingTimeStart || '',
            sendingTimeEnd: formState.sendingTimeEnd || '',
            pauseWeekends: formState.pauseWeekends || false
        } : {
            isBatchSend: false,
            scheduledEndDate: null,
            sendingTimeStart: '',
            sendingTimeEnd: '',
            pauseWeekends: false
        };

        const updatedItem = {
            ...selectedItem,
            contentId: contentId,
            contentType: contentType,
            contentName: contentName,
            recipientType: recipientData.recipientType,
            sendToId: recipientData.sendToId || tenancyId.toString(),
            scheduledFor: scheduledDate,
            ...batchSendFields
        };

        setCalendarItems(prev =>
            prev.map(item => item.id === selectedItem.id ? updatedItem : item)
        );

        trackItemUpdate(updatedItem);

        setUnsavedChanges(true);
    }, [selectedItem, formState, trackItemUpdate, tenancyId, recipientData]);

    const handleDeleteItem = useCallback(() => {
        if (!selectedItem) return;

        // Remove from calendar items
        setCalendarItems(prev =>
            prev.filter(item => item.id !== selectedItem.id)
        );

        if (mode === 'edit') {
            setChangeTracker(prev => {
                // If it's a new item (has temp ID), remove from newItems
                if (selectedItem.id.includes('temp-')) {
                    return {
                        ...prev,
                        newItems: prev.newItems.filter(item =>
                            !(item.contentId === selectedItem.contentId &&
                                item.scheduledFor === selectedItem.scheduledFor)
                        )
                    };
                } else {
                    // Otherwise add to items to remove
                    return {
                        ...prev,
                        itemsToRemove: [...prev.itemsToRemove, selectedItem.id],
                        // Remove from updatedItems if it's there
                        updatedItems: prev.updatedItems.filter(item =>
                            item.itemId !== selectedItem.id
                        )
                    };
                }
            });
        }

        // Also close the multi-item select modal
        setMultiItemSelectState(prev => ({
            ...prev,
            isOpen: false
        }));

        setUnsavedChanges(true);
    }, [selectedItem, mode]);

    // Save all changes using correct API
    const handleSaveChanges = useCallback(async () => {
        setAttemptedSave(true);

        if (mode === 'create' || mode === 'preset') {
            if (!deliveryGroupName.trim()) {
                showNotification('Please provide a name for the journey', 'error');
                return false;
            }

            if (calendarItems.length === 0) {
                showNotification('Please add at least one content item to the calendar', 'error');
                return false;
            }
        } else if (mode === 'edit' && deliveryGroup) {
            if (!deliveryGroup.name || !deliveryGroup.name.trim()) {
                showNotification('Journey name cannot be empty', 'error');
                return false;
            }
        }

        // Helper function to get the correct sendToId based on recipient type
        const getSendToId = () => {
            if (recipientData.recipientType === 'Tenancy') {
                return tenancyId.toString();
            }
            return recipientData.sendToId || '';
        };

        try {
            if (mode === 'edit' && deliveryGroup) {
                const sendToId = getSendToId();

                const requestData = {
                    name: deliveryGroup.name,
                    updatedById: deliveryGroup.createdById,
                    itemUpdates: updatedItems.map(item => ({
                        ...item,
                        recipientType: recipientData.recipientType,
                        sendToId: sendToId
                    })),
                    newItems: newItems.map(item => ({
                        ...item,
                        recipientType: recipientData.recipientType,
                        sendToId: sendToId
                    })),
                    itemIdsToRemove: itemsToRemove,
                    pausedContentTypes: deliveryGroup.pausedContentTypes || []
                };

                await updateDeliveryRequest({
                    tenancyId: tenancyId,
                    requestId: deliveryGroupId,
                    requestData: requestData
                }).unwrap();

                await refetchDeliveryRequest();
            } else if (mode === 'create' || mode === 'preset') {
                const user = JSON.parse(localStorage.getItem('user'));
                const userId = user ? user.email : '';
                const sendToId = getSendToId();

                const requestData = {
                    name: deliveryGroupName,
                    type: "Template",
                    createdById: userId,
                    deliveryItems: calendarItems.map(item => ({
                        contentId: item.contentId,
                        contentType: item.contentType,
                        contentName: item.contentName,
                        recipientType: recipientData.recipientType,
                        sendToId: sendToId,
                        scheduledFor: item.scheduledFor,
                        // Batch send fields
                        isBatchSend: item.isBatchSend || false,
                        scheduledEndDate: item.scheduledEndDate || null,
                        sendingTimeStart: item.sendingTimeStart || '',
                        sendingTimeEnd: item.sendingTimeEnd || '',
                        pauseWeekends: item.pauseWeekends || false
                    }))
                };

                await createDeliveryRequest({
                    tenancyId: tenancyId,
                    requestData: requestData
                }).unwrap();
            }

            setChangeTracker({
                updatedItems: [],
                itemsToRemove: [],
                newItems: []
            });
            setUnsavedChanges(false);

            showNotification(`Journey ${mode === 'create' || mode === 'preset' ? 'created' : 'updated'} successfully`, 'success');
            return true;
        } catch (error) {
            console.error(`Error ${mode === 'create' || mode === 'preset' ? 'creating' : 'saving changes to'} journey: `, error);
            showNotification(`Error ${mode === 'create' || mode === 'preset' ? 'creating' : 'saving changes to'} journey: ` + getDeliveryErrorMessage(error), 'error');
            return false;
        }
    }, [
        mode, deliveryGroup, deliveryGroupName, calendarItems,
        updatedItems, newItems, itemsToRemove,
        tenancyId, deliveryGroupId, updateDeliveryRequest, createDeliveryRequest,
        refetchDeliveryRequest, showNotification, setAttemptedSave, recipientData
    ]);

    return {
        // State
        monthsToDisplay,
        deliveryGroupName,
        setDeliveryGroupName,
        calendarItems,
        itemsByDate,
        batchRanges,
        isLoadingRequest,
        isUpdating,
        isCreating,
        unsavedChanges,
        attemptedSave,
        isAdmin,
        showBanner,
        setShowBanner,

        // Non-modal state 
        selectedDate,
        setSelectedDate,
        selectedItem,
        setSelectedItem,
        isMoveMode,
        setIsMoveMode,
        itemToMove,
        setItemToMove,

        // Modal state
        formState,
        multiItemSelectState,
        setMultiItemSelectState,

        // Recipient state
        recipientData,
        setRecipientData,
        isLoadingRecipientName,

        // Handlers
        handleFormChange,
        handleContentTypeChange,
        handleContentSelection,
        handleCollectionSelection,
        handleContentPackSelection,
        handleBatchSendChange,
        resetFormStateForNewItem,
        handleDayClick,
        handleItemClick,
        handleEnterMoveMode,
        handleCancelMoveMode,
        handleAddItem,
        handleUpdateItem,
        handleDeleteItem,
        handleSaveChanges,
        handleRecipientChange,
        trackItemUpdate,

        // Content pack data
        contentPackData
    };
};

export default useCalendarData;